import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'Shared/Hooks/useTranslation';

import { AddToPack } from 'Jsx/Shared/CourseCardV3/components/AddToPack';
import getImgxUrl from 'Utils/getImgxUrl';
import { useCoursesInBundleState } from 'Jsx/ContextApis/CoursesInBundle';

import TransactionInfo from '../TransactionInfo';
import { Course } from '../../types';
import locales from '../../i18n/localeEs';

import TextLink from 'crehana-ui/Buttons/TextLink';
import PlayArrow from 'crehana-icons/PlayArrow';
import ArrowLeft2 from 'crehana-icons/ArrowLeft2';

const translations = locales.es;

const Opacity = styled.div`
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 49.48%,
    rgba(0, 0, 0, 0.5) 100%
  );
`;
const Inner = styled.div`
  box-shadow: 0px 8px 18px rgba(92, 84, 122, 0.1);
`;

const CoverImage = styled.div<{
  src: string;
}>`
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  height: 218px;
`;

type FloatingCardProps = {
  className?: string;
  course: Course;
  showCourseTrailer(): void;
  isBuyable: boolean;
  CartButton: React.ReactElement;
  GiftButton: React.ReactElement;
  WishlistButton: React.ReactElement;
  ShareButton: React.ReactElement;
  canShowMembershipFeature: boolean;
  renderTransactionButtons(): React.ReactElement;
  isAppLoading: boolean;
  canRenderCountdown: boolean;
  userHasOrganization: boolean;
  showPaymentDialog(): void;
  canShowPaymentDialog: boolean;
  canShowAddToPackUI: boolean;
  canEnroll: boolean;
  courseTitle: string;
  isSticky: boolean;
};

const FloatingCard: FC<FloatingCardProps> = ({
  className,
  course,
  showCourseTrailer,
  isBuyable,
  CartButton,
  GiftButton,
  WishlistButton,
  ShareButton,
  canShowMembershipFeature,
  renderTransactionButtons,
  isAppLoading,
  canRenderCountdown,
  userHasOrganization,
  showPaymentDialog,
  canShowPaymentDialog,
  canShowAddToPackUI,
  canEnroll,
  isSticky,
}) => {
  const { t } = useTranslation();
  const {
    toggleCourseInBundle,
    coursesInBundle,
    isCoursesBundleComplete,
  } = useCoursesInBundleState();

  const coverImageUrl = getImgxUrl(course.promoImage, {
    w: 'auto',
    h: 218,
    crop: 'faces',
    q: 70,
    auto: 'format',
  });

  const Player = (
    <>
      <button
        id="course-detail-anchor"
        type="button"
        className="block relative w-full transition-all duration-500 ease-in"
        onClick={showCourseTrailer}
        style={{
          height: isSticky ? 0 : 218,
        }}
      >
        {!isSticky && (
          <>
            <CoverImage src={coverImageUrl} />
            <Opacity className="z-2 absolute w-full h-full top-0 left-0 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <PlayArrow size={40} color="#ffffff" />
                <p className="mt-24 font-link text-white">
                  {t('WATCH_TRAILER', {
                    defaultValue: translations.WATCH_TRAILER,
                  })}
                </p>
              </div>
            </Opacity>
          </>
        )}
      </button>
    </>
  );

  return (
    <div
      id="sticky-card"
      className={`${className}  z-5 md:pt-40`}
      style={{ top: -2, pointerEvents: 'all' }}
      data-cy="transaction-buttons-section"
    >
      <Inner className="bg-white">
        {Player}
        {canShowAddToPackUI ? (
          <div className="px-24 pb-48 text-center">
            <AddToPack
              courseInPack={coursesInBundle
                .map(el => el.originalId)
                .includes(course.originalId)}
              validateAddCustomBundle={() => {
                toggleCourseInBundle(course);
              }}
              fullCustomBundle={isCoursesBundleComplete}
            />
            <TextLink
              className="mt-20"
              onClick={() => {
                window.history.back();
              }}
              leftIcon={{
                component: ArrowLeft2,
                size: 13,
              }}
            >
              Ver más cursos
            </TextLink>
          </div>
        ) : (
          <TransactionInfo
            course={course}
            isBuyable={isBuyable}
            CartButton={CartButton}
            GiftButton={GiftButton}
            WishlistButton={WishlistButton}
            ShareButton={ShareButton}
            canShowMembershipFeature={canShowMembershipFeature}
            renderTransactionButtons={renderTransactionButtons}
            isAppLoading={isAppLoading}
            canRenderCountdown={canRenderCountdown}
            userHasOrganization={userHasOrganization}
            showPaymentDialog={showPaymentDialog}
            canShowPaymentDialog={canShowPaymentDialog}
            canEnroll={canEnroll}
            enrollmentUrl={course.enrollmentUrl}
          />
        )}
      </Inner>
    </div>
  );
};

export default FloatingCard;
